'use client'
import React, { memo } from 'react'
import classNames from 'classnames'
import { Card } from '@dustin-web/product-components/src/cards/product-card-gallery-composable'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import { getProductLabels } from '@dustin-web/product-components/src/cards/utils'
import { useTrackingOnScreen } from '@dustin-web/microsite-components/src/on-screen'
import ProductPrice from '@dustin-web/product-components/src/product-price'
import { Checkbox } from '@dustin-web/microsite-components/src/atoms'
import EnergyLabel from '@dustin-web/product-components/src/energy-label'
import type {
  DigitalDataOrigin,
  ProductWithCategoryType,
} from '@dustin-web/product-components/src/utils/digital-data-types'
import type { ProductTypeProps } from '@dustin-web/product-components/src/cards'
import type { Product } from '@productsite-beta/src/graphql/generated/graphql-types'

import './product-card.scss'

type Props = {
  imagePriority?: boolean
  product: Product
  ticket: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex: number
  showAddToCartButton?: boolean
  sizeClasses?: string
  className?: string
  isSelected: boolean
  isSmallScreen: boolean
  handleCheckbox: () => void
}

const ProductCard = ({
  product,
  ticket,
  imagePriority = false,
  digitalDataOrigin,
  digitalIndex,
  sizeClasses,
  className,
  isSelected,
  isSmallScreen,
  handleCheckbox,
}: Props) => {
  const ref = useTrackingOnScreen(() => pushViewItemList(digitalIndex, digitalDataOrigin, product))

  if (!product) {
    return null
  }

  return (
    <div
      ref={ref}
      className={classNames(className, sizeClasses, 'product-card', { mobile: isSmallScreen })}>
      <Checkbox
        id={!isSelected ? 'selected' : 'unselected'} //gtm
        classes={(product as any).productName} //gtm
        checked={isSelected}
        onChange={handleCheckbox}
      />
      <Card.Picture
        className="product-image"
        product={product}
        ticket={ticket}
        imageWidth={40}
        imageHeight={40}
        imagePriority={imagePriority}
        digitalDataIndex={digitalIndex}
        digitalDataOrigin={digitalDataOrigin}
      />
      <div className="product-info">
        <div className="u-w-100">
          <Card.Title
            product={{ ...product, displaySpecifications: '' }}
            hideManufacturer
            ticket={ticket}
            digitalDataIndex={digitalIndex}
            digitalDataOrigin={digitalDataOrigin}
            compact
            ellipsis
          />
          <EnergyLabel
            className="u-pt-8"
            productId={product.productErpIdentifier}
            nameSlug={product.nameSlug}
            productTitle={product.displayName || ''}
            energyClass={product.energyClass}
            energyLabelImageId={product.energyLabelImageId}
            width={34}
            textAlignment="right"
          />
          {isSmallScreen && (
            <ProductPrice
              className="d-flex u-pt-16"
              priceClassName="u-fs-16"
              campaignClassName="u-fs-16 u-mr-8 u-fw-lighter"
              price={product.price}
              displayVatInfo={false}
            />
          )}
          <div className="d-flex">
            <Card.Availability
              compactStatus
              containerClassNames="product-availability"
              availability={product.availability}
              isBid={product.price?.isBid}
              defaultContainerClassName="d-flex"
            />
          </div>
        </div>
        {!isSmallScreen && (
          <div className="product-price align-items-center d-flex justify-content-center">
            <ProductPrice
              className="d-flex flex-column"
              priceClassName="u-fs-16"
              campaignClassName="u-fs-16 u-fw-lighter"
              price={product.price}
              displayVatInfo={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function pushViewItemList(
  digitalIndex?: number,
  digitalDataOrigin?: DigitalDataOrigin,
  product?: ProductWithCategoryType | ProductTypeProps | null
) {
  if (product && digitalDataOrigin) {
    ProductDigitalData.pushViewItemList(
      [
        {
          ...product,
          productListIndex: digitalIndex,
          label: getProductLabels(product as ProductTypeProps),
        },
      ] as ProductWithCategoryType[],
      digitalDataOrigin
    )
  }
}

export default memo(ProductCard)
